// 🧩 Policies.jsx – Central hub: view switch + country filter + shared logic
import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import PolicyRoadmap from "./PolicyRoadmap";
import PolicyList from "./PolicyList";
import { Toast, ToastContainer } from "react-bootstrap";
import "./Policies.css";

const countryOptions = [
  { code: "BE", name: "Belgium" },
  { code: "DE", name: "Germany" },
  { code: "FR", name: "France" },
  { code: "CH", name: "Switzerland" },
  { code: "US", name: "United States" },
  { code: "IT", name: "Italy" },
  { code: "IE", name: "Ireland" },
  { code: "ES", name: "Spain" },
  { code: "GR", name: "Greece" }
];

export default function Policies({ userCountryCode }) {
  const [policies, setPolicies] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(userCountryCode?.toUpperCase() || "BE");
  const [viewMode, setViewMode] = useState("roadmap"); // "roadmap" | "list"
  const [toast, setToast] = useState({ show: false, message: "", variant: "success" });
  const inputRefs = useRef({});
  const debounceTimeout = useRef(null);

  const isEditableUser = selectedCountry === userCountryCode?.toUpperCase();

  const showToast = (message, variant = "success") => {
    setToast({ show: true, message, variant });
    setTimeout(() => setToast(prev => ({ ...prev, show: false })), 3000);
  };

  useEffect(() => {
    const fetchPolicies = async () => {
      try {
        const res = await axios.get("/api/iso_policies.php", {
          params: { country: selectedCountry },
          withCredentials: true
        });
        if (res.data.success) {
          const enriched = res.data.data.map(p => ({
            ...p,
            country_code: selectedCountry
          }));
          setPolicies(enriched);
          showToast(`Policies loaded for ${selectedCountry}`);
        } else {
          showToast("Error loading policies", "danger");
        }
      } catch (err) {
        console.error(err);
        showToast("Network error", "danger");
      }
    };

    fetchPolicies();
  }, [selectedCountry]);

  const savePolicy = useCallback((policy) => {
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    debounceTimeout.current = setTimeout(async () => {
      try {
        await axios.post("/api/iso_policies.php", policy, {
          withCredentials: true,
          headers: { "Content-Type": "application/json" }
        });
        showToast(`Saved: ${policy.policy}`);
      } catch (err) {
        console.error(err);
        showToast(`Error saving: ${policy.policy}`, "danger");
      }
    }, 300);
  }, []);

  const handleChange = (index, field, value) => {
    const updated = [...policies];
    updated[index][field] = value;
    setPolicies(updated);
    savePolicy(updated[index]);
  };

  const exportCSV = () => {
    const headers = ["Clause", "Policy", "Description", "Completion Status", "Last Review", "Requires Policy"];
    const rows = policies.map(p => [
      p.clause,
      p.policy,
      p.description,
      p.completion_status,
      p.last_review || "",
      p.requires_policy ? "Yes" : "No"
    ]);
    const csv = [headers, ...rows].map(row => row.map(f => `"${f}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `iso_policies_${selectedCountry}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);

    showToast(`Exported policies for ${selectedCountry}`);
  };

  return (
    <div className="policy-wrapper">
      <div className="policy-header">
        <h2 className="policy-title">ISO/IEC 27001 – {selectedCountry}</h2>
        <div className="policy-controls">
          <select
            className="policy-country-select"
            value={selectedCountry}
            onChange={(e) => setSelectedCountry(e.target.value)}
          >
            {countryOptions.map((c) => (
              <option key={c.code} value={c.code}>
                {c.name}
              </option>
            ))}
          </select>
          <button className="policy-export-button" onClick={exportCSV}>
            Export CSV
          </button>
          <div className="policy-view-toggle">
            <button
              onClick={() => setViewMode("roadmap")}
              className={viewMode === "roadmap" ? "active" : ""}
            >
              Roadmap
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={viewMode === "list" ? "active" : ""}
            >
              List
            </button>
          </div>
        </div>
      </div>

      {viewMode === "roadmap" ? (
        <PolicyRoadmap policies={policies} />
      ) : (
        <PolicyList
          policies={policies}
          isEditableUser={isEditableUser}
          handleChange={handleChange}
          inputRefs={inputRefs}
        />
      )}

      <ToastContainer position="bottom-end" className="p-3" style={{ zIndex: 9999 }}>
        <Toast
          show={toast.show}
          bg={toast.variant}
          onClose={() => setToast(prev => ({ ...prev, show: false }))}
          delay={3000}
          autohide
        >
          <Toast.Body className="text-white">{toast.message}</Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
}
